function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import React, { useCallback, useMemo } from 'react';
import { Icon, icons, Button, Flex, Box, Colors } from '@tapresearch/ui/components';
import { Buttons } from 'Shared/Button';
var activeColor = Colors.blue[500];
var paginationButtonStyles = function(param) {
    var active = param.active, onClick = param.onClick;
    return {
        outline: 'none',
        bg: active ? activeColor : Colors.white,
        border: !active ? '2px solid' : 'none',
        borderColor: Colors.gray[200],
        color: active ? Colors.white : Colors.black,
        pointerEvents: onClick ? 'all' : 'none',
        boxShadow: 'none',
        minWidth: '32px',
        height: 8,
        padding: 1,
        variant: 'subtle',
        _hover: {
            bg: active ? activeColor : Colors.blue[100]
        }
    };
};
var paginationNavButtonStyles = function(props) {
    return _object_spread_props(_object_spread({}, paginationButtonStyles(props), Buttons.SecondarySmall), {
        height: 8,
        _disabled: _object_spread_props(_object_spread({}, Buttons.SecondarySmall['_disabled']), {
            bg: 'gray.700',
            color: 'gray.100',
            _hover: {
                bg: 'gray.700',
                color: 'gray.100'
            }
        })
    });
};
function PaginationButton(props) {
    var page = props.page, onClick = props.onClick, children = props.children, _props_disabled = props.disabled, disabled = _props_disabled === void 0 ? false : _props_disabled, navButton = props.navButton, testId = props['data-testid'], active = props.active;
    var innerOnClick = useCallback(function() {
        if (onClick) {
            onClick(page);
        }
    }, [
        page,
        onClick
    ]);
    return /*#__PURE__*/ React.createElement(Box, {
        as: "li",
        mr: 2
    }, /*#__PURE__*/ React.createElement(Button, _object_spread({
        isDisabled: disabled,
        onClick: innerOnClick,
        "data-testid": testId || "pagination-button-".concat(page).concat(active ? '-active' : '')
    }, (navButton ? paginationNavButtonStyles : paginationButtonStyles)(props)), children || page));
}
var range = function(start, end) {
    return Array.from({
        length: end - start + 1
    }, function(v, k) {
        return k + start;
    });
};
export function Pagination(param) {
    var currentPage = param.currentPage, maxPagesToShow = param.maxPagesToShow, pageCount = param.pageCount, _param_onPageChange = param.onPageChange, onPageChange = _param_onPageChange === void 0 ? function() {
    /* do nothing */ } : _param_onPageChange;
    var onPaginationButtonClick = useCallback(function(page) {
        onPageChange(page);
    }, [
        onPageChange
    ]);
    var _useMemo = useMemo(function() {
        if (pageCount <= 1 || pageCount === undefined || currentPage === undefined) {
            return {};
        }
        var leftDots = false;
        var rightDots = false;
        var navBegin = 1;
        var navEnd = maxPagesToShow;
        var middleMaxPagesToShow = Math.floor(maxPagesToShow / 2.0);
        if (pageCount > maxPagesToShow) {
            if (currentPage > middleMaxPagesToShow + 1) {
                leftDots = true;
                navBegin = currentPage - middleMaxPagesToShow + 1;
            }
            if (currentPage < pageCount - middleMaxPagesToShow) {
                rightDots = true;
                navEnd = navBegin + maxPagesToShow - (!leftDots ? 3 : 4);
                if (leftDots) {
                    navBegin += 1;
                }
            } else {
                navEnd = pageCount;
                navBegin = pageCount - maxPagesToShow + 3;
            }
        } else {
            navEnd = pageCount;
        }
        if (pageCount === 0) {
            return null;
        }
        return {
            leftDots: leftDots,
            rightDots: rightDots,
            enabledPrevPage: currentPage > 1,
            enabledNextPage: currentPage < pageCount,
            toRenderPages: range(navBegin, navEnd)
        };
    }, [
        currentPage,
        pageCount,
        maxPagesToShow
    ]), leftDots = _useMemo.leftDots, rightDots = _useMemo.rightDots, enabledPrevPage = _useMemo.enabledPrevPage, enabledNextPage = _useMemo.enabledNextPage, toRenderPages = _useMemo.toRenderPages;
    return pageCount > 1 ? /*#__PURE__*/ React.createElement(Flex, {
        "data-testid": "pagination-container",
        as: "nav",
        justifyContent: "flex-end"
    }, /*#__PURE__*/ React.createElement(Flex, {
        as: "ul",
        __css: {
            display: 'flex',
            listStyle: 'none'
        }
    }, /*#__PURE__*/ React.createElement(PaginationButton, {
        disabled: !enabledPrevPage,
        page: currentPage - 1,
        navButton: true,
        "data-testid": "prev-page-button",
        onClick: onPaginationButtonClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        boxSize: 5,
        icon: icons.fi.ChevronLeft
    })), leftDots && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PaginationButton, {
        page: 1,
        onClick: onPaginationButtonClick
    }), /*#__PURE__*/ React.createElement(PaginationButton, {
        "data-testid": "pagination-ellipsis"
    }, "...")), toRenderPages.map(function(page) {
        return /*#__PURE__*/ React.createElement(PaginationButton, {
            key: "pagination-button-".concat(page),
            page: page,
            onClick: onPaginationButtonClick,
            active: page === currentPage
        });
    }), rightDots && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PaginationButton, {
        "data-testid": "pagination-ellipsis"
    }, "..."), /*#__PURE__*/ React.createElement(PaginationButton, {
        page: pageCount,
        onClick: onPaginationButtonClick
    })), /*#__PURE__*/ React.createElement(PaginationButton, {
        disabled: !enabledNextPage,
        page: currentPage + 1,
        navButton: true,
        onClick: onPaginationButtonClick,
        "data-testid": "next-page-button"
    }, /*#__PURE__*/ React.createElement(Icon, {
        boxSize: 5,
        icon: icons.fi.ChevronRight
    })))) : null;
}
